import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  apiRequest,
  clearSession,
  loadSession,
  login as apiLogin,
  logout as apiLogout,
  setSessionLostHandler,
} from './api'
import type { Profile, StaffInfo } from './apiTypes'

interface SessionValue {
  /** True while the stored session is being validated at startup. */
  loading: boolean
  profile: Profile | null
  /** Dashboard access. `null` for a valid account with no staff membership. */
  staff: StaffInfo | null
  isSignedIn: boolean
  isAdmin: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(() => loadSession() !== null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [isSignedIn, setIsSignedIn] = useState(() => loadSession() !== null)

  const forgetSession = useCallback(() => {
    clearSession()
    setProfile(null)
    setIsSignedIn(false)
    setLoading(false)
  }, [])

  // A refresh that finally fails inside any request lands here rather than leaving
  // the app apparently signed in behind a wall of failures.
  useEffect(() => {
    setSessionLostHandler(forgetSession)
    return () => setSessionLostHandler(null)
  }, [forgetSession])

  const loadProfile = useCallback(async () => {
    // Hitting this endpoint is also what activates a principal's invited membership,
    // so it is the whole of the staff bootstrap — there is no "accept invitation"
    // step to build (26-school-admin-api-handoff.md §2).
    const fetched = await apiRequest<Profile>('/users/profile/')
    setProfile(fetched)
    setIsSignedIn(true)
  }, [])

  useEffect(() => {
    if (!loadSession()) return
    let cancelled = false

    void (async () => {
      try {
        await loadProfile()
      } catch {
        if (!cancelled) forgetSession()
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [forgetSession, loadProfile])

  const signIn = useCallback(
    async (email: string, password: string) => {
      await apiLogin(email, password)
      await loadProfile()
    },
    [loadProfile],
  )

  const signOut = useCallback(async () => {
    await apiLogout()
    forgetSession()
  }, [forgetSession])

  const value = useMemo<SessionValue>(
    () => ({
      loading,
      profile,
      staff: profile?.staff ?? null,
      isSignedIn,
      isAdmin: profile?.staff?.role === 'admin',
      signIn,
      signOut,
    }),
    [loading, profile, isSignedIn, signIn, signOut],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession must be used inside a SessionProvider')
  return value
}
